import { Box, Fab } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import RecipeItem from './RecipeItem';
import RecipeSortBar, { RecipeSort } from './RecipeSortBar';
import { Recipe } from '../../models/Recipe';
import { getAllRecipes } from '../../utils/RecipeService';
import Cookies from 'js-cookie';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { PullToRefresh } from '../general/PullToRefresh';

export type RecipeOverviewProps = {
  filter: string;
}

const RecipeOverview = (props: RecipeOverviewProps) => {
  const [allRecipes, setAllRecipes] = useState<Recipe[]>([]);
  const [sort, setSort] = useState<RecipeSort>('name');
  const navigate = useNavigate();

  const loadAllRecipes = useCallback(() => {
    const token: string | undefined = Cookies.get('token');
    if (token) {
      getAllRecipes().then((response: Recipe[]) => setAllRecipes(response));
    }
  }, []);

  useEffect(() => {
    loadAllRecipes();
  }, [loadAllRecipes]);

  const visibleRecipes = useMemo(() => {
    const filter = props.filter.toLowerCase();
    const matches = allRecipes.filter(r =>
      r.name.toLowerCase().includes(filter) || r.tags.some(t => t.toLowerCase().includes(filter))
    );

    const byName = (r1: Recipe, r2: Recipe) => r1.name.localeCompare(r2.name);

    switch (sort) {
      case 'cookTime':
        // Recipes without a cook time sort last rather than jumping to the front as 0.
        return matches.sort((r1, r2) => {
          const diff = (r1.cookTime || Number.MAX_SAFE_INTEGER) - (r2.cookTime || Number.MAX_SAFE_INTEGER);
          return diff !== 0 ? diff : byName(r1, r2);
        });
      case 'newest':
        // There is no created-at column; IDs are generated by an identity column, so a descending
        // ID is a faithful stand-in for insertion order.
        return matches.sort((r1, r2) => (r2.id ?? 0) - (r1.id ?? 0));
      default:
        return matches.sort(byName);
    }
  }, [allRecipes, props.filter, sort]);

  return (
    <>
      <PullToRefresh onRefresh={loadAllRecipes} style={{ height: 'calc(100vh - 64px)' }}>
        <RecipeSortBar value={sort} onChange={setSort} />

        <Box sx={{ px: 1.5, pt: 1.5, pb: 12, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {visibleRecipes.map((recipe: Recipe) => (
            <RecipeItem key={recipe.id} recipe={recipe} />
          ))}
        </Box>
      </PullToRefresh>

      <Fab
        color="primary"
        aria-label="add recipe"
        sx={{ position: 'fixed', bottom: 28, right: 24 }}
        onClick={() => navigate('/recipe/edit/-1')}
      >
        <AddIcon />
      </Fab>
    </>
  );
};

export default RecipeOverview;
